/**
 * Composite Control → Since → Verify presentation.
 * Reuses physical Overview Control / Since vocabulary. No causal claims.
 */
import { COMPOSITE_SUBJECT_KIND } from './overviewV2CompositeNow';
import type { OverviewPeriodConditionRow, OverviewSinceControlRef } from '../continuousSafety/overviewSiteConditionSummary';
import { buildConditionLifecyclePath } from '../continuousSafety/conditionLifecycleNavigation';

export const COMPOSITE_CONTROL_OBJECTIVE_EXAMPLE =
  'Separate pedestrian and forklift movement in Warehouse Room B';

export type CompositeLifecycleOverlay = {
  conditionId: string;
  controlId?: string | null;
  controlRevisionId?: string | null;
  controlObjective?: string | null;
  implementedAt?: string | null;
  sinceCount?: number | null;
  sinceSummary?: string | null;
  latestOccurredAt?: string | null;
  comparability?: string | null;
  verificationId?: string | null;
  resultDirection?: string | null;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

export function buildCompositeLifecyclePath(args: {
  siteId: string;
  compositeRuleId: string;
  scopeType: string;
  scopeId: string;
  cameraId?: string | null;
  focus?: 'control' | 'since' | 'scan' | null;
  compositeRuleName?: string | null;
}): string {
  const params = new URLSearchParams();
  if (args.siteId) params.set('site_id', args.siteId);
  params.set('subject_kind', COMPOSITE_SUBJECT_KIND);
  params.set('composite_rule_id', args.compositeRuleId);
  params.set('composite_scope_type', args.scopeType || 'camera');
  params.set('composite_scope_id', args.scopeId);
  if (args.cameraId) params.set('camera_id', args.cameraId);
  if (args.compositeRuleName) params.set('composite_rule_name', args.compositeRuleName);
  if (args.focus) params.set('focus', args.focus);
  return `/acceptance/lifecycle?${params.toString()}`;
}

export function compositeSinceControlRef(overlay: CompositeLifecycleOverlay | null | undefined): OverviewSinceControlRef | undefined {
  if (!overlay?.controlId && !overlay?.controlRevisionId) return undefined;
  const notComparable = text(overlay.comparability) === 'not_comparable';
  return {
    controlId: overlay.controlRevisionId || overlay.controlId || null,
    anchorStartedAt: overlay.implementedAt || null,
    observedThrough: overlay.latestOccurredAt || null,
    availability: overlay.controlId || overlay.controlRevisionId ? 'available' : 'missing',
    comparisonStatus: notComparable ? 'not_comparable' : (overlay.sinceCount != null ? 'comparable' : 'follow_up_open'),
    bucketCount: overlay.sinceCount || 0,
  };
}

export function presentCompositeSinceCopy(count: number): string {
  const noun = count === 1 ? 'occurrence' : 'occurrences';
  return `${count} ${noun} observed since the control was recorded.`;
}

export function presentCompositeVerifyCopy(beforeCount: number, afterCount: number): string {
  return `Before ${beforeCount} occurrences / After ${afterCount} in comparable windows.`;
}

export function compositeControlRecordPath(args: {
  siteId: string;
  compositeRuleId: string;
  scopeType: string;
  scopeId: string;
  cameraId?: string | null;
  hasControl?: boolean;
  compositeRuleName?: string | null;
}): string {
  return buildCompositeLifecyclePath({
    ...args,
    focus: args.hasControl ? 'control' : 'control',
  });
}

export function mergeOverviewV2CompositeLifecycle(
  rows: OverviewPeriodConditionRow[],
  overlays: CompositeLifecycleOverlay[] | null | undefined,
): OverviewPeriodConditionRow[] {
  if (!overlays?.length) return rows;
  const byId = new Map(overlays.map((item) => [item.conditionId, item]));
  return rows.map((row) => {
    const overlay = byId.get(row.conditionId);
    if (!overlay) return row;
    const sinceControl = compositeSinceControlRef(overlay);
    return {
      ...row,
      sinceControl: sinceControl || row.sinceControl,
      lifecycle: overlay.controlRevisionId
        ? {
          latestImmediateIncident: null,
          latestTemporalPatternIncident: null,
          motivatingIncidentIds: [],
          controlRevisionId: overlay.controlRevisionId,
          sinceStartedAt: overlay.implementedAt || null,
          latestScan: null,
          lifecycleHref: null,
          lifecycleUiPath: null,
          populationExplanation: overlay.sinceSummary || null,
          postControlPattern: null,
        }
        : row.lifecycle,
    };
  });
}

export function isCompositeLifecycleHref(path: string | null | undefined): boolean {
  const value = text(path);
  return value.includes('subject_kind=COMPOSITE') || value.includes('subject_kind=COMPOSITE'.toLowerCase());
}

export { buildConditionLifecyclePath };
